// src/hooks/useMidi.ts
import { useState, useEffect, useCallback } from 'react';
import { WebMidi, Input } from 'webmidi';
import { MidiNote, MidiInputDevice } from '../types/midi';
import { useSettings } from '../contexts/SettingsContext';
import logger from '../utils/logger';

let soundUtils: typeof import('../utils/soundUtils') | null = null;

export const useMidi = () => {
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [inputs, setInputs] = useState<MidiInputDevice[]>([]);
  const [activeNotes, setActiveNotes] = useState<MidiNote[]>([]);
  const [selectedInput, setSelectedInput] = useState<Input | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { isSoundEnabled } = useSettings();

  // Update available inputs
  const updateInputs = useCallback(() => {
    if (!WebMidi.enabled) return;

    const midiInputs = WebMidi.inputs.map(input => ({
      id: input.id,
      name: input.name,
      manufacturer: input.manufacturer
    }));

    setInputs(midiInputs);

    // If the previously selected input is no longer available, clear it
    if (selectedInput && !WebMidi.inputs.some(input => input.id === selectedInput.id)) {
      setSelectedInput(null);
    }
  }, [selectedInput]);

  // Handle note on event
  const handleNoteOn = useCallback((e: any) => {
    const newNote: MidiNote = {
      number: e.note.number,
      velocity: e.note.rawAttack,
      name: e.note.name,
      octave: e.note.octave,
      invalidated: false,
      source: 'midi'
    };

    if (isSoundEnabled && !soundUtils) {
      soundUtils = require('../utils/soundUtils');
    }
    if (isSoundEnabled && soundUtils) {
      soundUtils.playNote(newNote, 30.0);
    }

    setActiveNotes(prevNotes => {
      const existingNoteIndex = prevNotes.findIndex(
        note => note.number === newNote.number && note.source === 'midi'
      );
      if (existingNoteIndex >= 0) {
        const updatedNotes = [...prevNotes];
        updatedNotes[existingNoteIndex] = newNote;
        return updatedNotes;
      }
      return [...prevNotes, newNote];
    });
  }, [isSoundEnabled]);

  // Handle note off event
  const handleNoteOff = useCallback((e: any) => {
    setActiveNotes(prevNotes =>
      prevNotes.filter(
        note => note.number !== e.note.number || note.source !== 'midi'
      )
    );
    if (isSoundEnabled && soundUtils) {
      soundUtils.stopNote(e.note.number);
    }
  }, [isSoundEnabled]);

  // Connect to a MIDI input
  const connectToInput = useCallback((inputId: string) => {
    if (!WebMidi.enabled) return;

    // Clean up previous input listeners
    if (selectedInput) {
      selectedInput.removeListener('noteon');
      selectedInput.removeListener('noteoff');
    }

    // If empty inputId is passed, just disconnect current device
    if (!inputId) {
      setSelectedInput(null);
      setActiveNotes(prevNotes =>
        prevNotes.filter(note => note.source !== 'midi')
      );
      return true;
    }

    const input = WebMidi.getInputById(inputId);
    if (!input) {
      setError(`Input with ID ${inputId} not found`);
      return false;
    }

    // Set up new input listeners
    input.addListener('noteon', handleNoteOn);
    input.addListener('noteoff', handleNoteOff);

    setSelectedInput(input);
    setActiveNotes(prevNotes =>
      prevNotes.filter(note => note.source !== 'midi')
    );

    logger.debug({ deviceName: input.name, deviceId: inputId }, 'MIDI device connected');
    return true;
  }, [selectedInput, handleNoteOn, handleNoteOff]);

  // ADD THIS NEW EFFECT: Update event listeners when callbacks change
  useEffect(() => {
    if (selectedInput && WebMidi.enabled) {
      // Remove old listeners
      selectedInput.removeListener('noteon');
      selectedInput.removeListener('noteoff');

      // Add new listeners with updated callbacks
      selectedInput.addListener('noteon', handleNoteOn);
      selectedInput.addListener('noteoff', handleNoteOff);
    }
  }, [selectedInput, handleNoteOn, handleNoteOff]);

  // Initialize WebMidi
  useEffect(() => {
    WebMidi.enable({ sysex: true })
      .then(() => {
        setIsEnabled(true);
        updateInputs();

        // Listen for device connection/disconnection
        WebMidi.addListener('connected', updateInputs);
        WebMidi.addListener('disconnected', updateInputs);
      })
      .catch(err => {
        setError(`WebMidi could not be enabled: ${err}`);
      });

    return () => {
      WebMidi.removeListener('connected');
      WebMidi.removeListener('disconnected');
    };
  }, [updateInputs]);

  // Select MIDI input
  const selectInput = useCallback((inputId: string) => {
    if (!WebMidi.enabled) return;
    connectToInput(inputId);
  }, [connectToInput]);

  return {
    isEnabled,
    inputs,
    activeNotes,
    selectedInput,
    selectInput,
    error
  };
};
